/**
 * The password rule, as one decision.
 *
 * The four criteria and the special-character set are reproduced from the mobile app
 * (`features/auth/hooks/usePasswordStrength.ts` and `usePasswordValidity.ts`), not approximated: the
 * same account is created from either client, so a password this app accepts and mobile rejects — or
 * a checklist that disagrees between the two — is a defect the trainer meets at the worst possible
 * moment. The set is deliberately explicit rather than "not alphanumeric", which would count a space
 * or a Polish diacritic as a special character.
 *
 * **All four criteria are required**, and the CTA gate is the same rule the checklist draws. That
 * lockstep is the point of exposing one function rather than four predicates: a screen that combined
 * them itself could enable the button while a row still showed a cross, which is exactly the defect
 * mobile's `usePasswordValidity` header records having fixed.
 *
 * Supabase applies its own project-level policy on top of this. A password that passes here and is
 * refused there comes back as `weak_password` and is surfaced by the shared error mapping — this
 * module does not try to predict it.
 *
 * Pure: two strings in, one object out. No React, no DOM, no i18n.
 */

const MINIMUM_LENGTH = 8;

/** Mobile's `SPECIAL_CHAR_REGEX`, character for character. */
const SPECIAL_CHARACTER = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/;

export interface PasswordCriteria {
  length: boolean;
  uppercase: boolean;
  digit: boolean;
  special: boolean;
}

/** How the confirmation field relates to the password. `pending` means it is still empty. */
export type ConfirmationState = 'pending' | 'match' | 'mismatch';

/** The tier word under the meter. `null` while nothing is typed, so the row stays quiet. */
export type PasswordTier = 'weak' | 'fair' | 'good' | 'strong';

export interface PasswordAssessment {
  criteria: PasswordCriteria;
  /** How many criteria are met, 0–4. The meter fills this many of its four segments. */
  metCount: number;
  strength: PasswordTier | null;
  confirmation: ConfirmationState;
  /** The CTA gate: all four criteria, and a confirmation that matches. */
  acceptable: boolean;
}

const STRENGTH_BY_MET_COUNT: readonly PasswordTier[] = ['weak', 'fair', 'good', 'strong'];

export function assessPassword(password: string, confirmation: string): PasswordAssessment {
  const criteria: PasswordCriteria = {
    length: password.length >= MINIMUM_LENGTH,
    uppercase: /[A-Z]/.test(password),
    digit: /[0-9]/.test(password),
    special: SPECIAL_CHARACTER.test(password),
  };

  const metCount = Object.values(criteria).filter(Boolean).length;

  const confirmationState: ConfirmationState =
    confirmation.length === 0 ? 'pending' : password === confirmation ? 'match' : 'mismatch';

  return {
    criteria,
    metCount,
    strength: metCount === 0 ? null : (STRENGTH_BY_MET_COUNT[metCount - 1] ?? null),
    confirmation: confirmationState,
    acceptable: metCount === STRENGTH_BY_MET_COUNT.length && confirmationState === 'match',
  };
}
